/**
 * Pure builders for CREATING a content-bearing workspace pin from a live tab
 * (Task 18). Kept side-effect-free so the create/update paths in
 * `WorkspacePinButton` stay thin and the auto-naming / payload rules are unit
 * testable without rendering.
 *
 * Auto-naming (spec):
 *  - tableView → `${tableName} — filtered` when filters are present, else `tableName`.
 *  - query / notebook snapshot → the tab title.
 *  - file-backed query / notebook → the file basename.
 *
 * G4 — the `source:'file'` payload is chosen HERE, at creation, from the tab's
 * live `filePath`. Divergence later treats file pins as inert (G1); this module
 * never runs again for an existing pin.
 */

import type {
  PinnedWorkspaceRef,
  PinnedQueryPayload,
  PinnedNotebookPayload,
} from '../../../../types/electron';
import type { CapturedDefinition } from '../../../database-objects/services/pinnedObjects/pinDivergence';
import { autoComparePinName } from './comparePin';

/**
 * The `workspaceType` an analytics event reports for a pin. Uses the
 * `WorkspaceType` literals (`table-explorer`, `data-compare`, …) rather than
 * the on-disk pin kinds, so the events line up with every other workspace
 * event we fire.
 */
export type PinWorkspaceType =
  | 'table-explorer'
  | 'query'
  | 'notebook'
  | 'data-compare'
  | 'schema-compare';

/** Low-cardinality analytics props for `feature:workspace_pin*` events. */
export type WorkspacePinTelemetry = {
  workspaceType: PinWorkspaceType;
  source: 'file' | 'snapshot';
};

/** Pin kind → the `WorkspaceType`-shaped label used in analytics + tooltips. */
export function pinWorkspaceType(kind: PinnedWorkspaceRef['kind']): PinWorkspaceType {
  switch (kind) {
    case 'tableView': return 'table-explorer';
    case 'dataCompare': return 'data-compare';
    case 'schemaCompare': return 'schema-compare';
    default: return kind;
  }
}

/** Last path segment, tolerant of both `/` and `\` separators. */
export function basename(filePath: string): string {
  const trimmed = filePath.replace(/[/\\]+$/, '');
  const idx = Math.max(trimmed.lastIndexOf('/'), trimmed.lastIndexOf('\\'));
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}

/** Auto-generated pin name for a fresh pin (see module header). */
export function autoWorkspacePinName(
  captured: CapturedDefinition,
  tabTitle: string,
  filePath: string | undefined,
): string {
  if (captured.workspaceType === 'table-explorer') {
    const { tableName, filters } = captured.payload;
    return filters.length > 0 ? `${tableName} — filtered` : tableName;
  }
  // Comparisons name themselves from their two endpoints — the tab title is
  // often the generic "Schema Compare", which reads as noise in the tree.
  if (captured.workspaceType === 'data-compare' || captured.workspaceType === 'schema-compare') {
    return autoComparePinName(captured);
  }
  // query / notebook: file basename when file-backed, otherwise the tab title.
  if (filePath) return basename(filePath);
  return tabTitle;
}

/**
 * Build a fresh `PinnedWorkspaceRef` from a captured live definition. The
 * envelope `database` comes from the tab's connection; for tableView the
 * payload's own `database` is aligned to it so the envelope/payload equality
 * (F10) always holds. Returns a new UUID id each call.
 */
export function buildPinnedWorkspaceRef(args: {
  captured: CapturedDefinition;
  database: string;
  tabTitle: string;
  filePath?: string;
}): PinnedWorkspaceRef {
  const { captured, database, tabTitle, filePath } = args;
  const id = crypto.randomUUID();
  const name = autoWorkspacePinName(captured, tabTitle, filePath);

  if (captured.workspaceType === 'table-explorer') {
    return { kind: 'tableView', id, name, database, payload: { ...captured.payload, database } };
  }

  if (captured.workspaceType === 'data-compare') {
    return { kind: 'dataCompare', id, name, database, payload: captured.payload };
  }

  if (captured.workspaceType === 'schema-compare') {
    return { kind: 'schemaCompare', id, name, database, payload: captured.payload };
  }

  if (captured.workspaceType === 'query') {
    const payload: PinnedQueryPayload = filePath ? { source: 'file', filePath } : captured.payload;
    return { kind: 'query', id, name, database, payload };
  }

  const payload: PinnedNotebookPayload = filePath ? { source: 'file', filePath } : captured.payload;
  return { kind: 'notebook', id, name, database, payload };
}

/**
 * Build an `updatePin` payload patch that re-snapshots the pin from the current
 * live definition (used by the diverged "Update" action).
 *
 * F1 (Codex gate-3 fix) — Update NEVER converts a pin's flavor. It only ever
 * runs for a DIVERGED pin, and a file-source pin never diverges (G1 in
 * `pinDivergence.ts`), so this only ever fires for a text/cells snapshot pin.
 * Always echo the live captured snapshot payload back — there is no
 * `filePath` branch here (query/notebook never had one that mattered; the
 * file-vs-snapshot decision (G4) is made ONCE, at creation, in
 * `buildPinnedWorkspaceRef`).
 */
export function buildWorkspacePinPatch(args: {
  captured: CapturedDefinition;
  database: string;
}): { database: string; payload: PinnedWorkspaceRef['payload'] } {
  const { captured, database } = args;
  if (captured.workspaceType === 'table-explorer') {
    return { database, payload: { ...captured.payload, database } };
  }
  return { database, payload: captured.payload };
}

/**
 * True for query/notebook pins bound to a file on disk. Compare and tableView
 * pins are always snapshots — their payload's `source` field is an ENDPOINT,
 * not a `'file' | 'text' | 'cells'` discriminant, so it must never be sniffed
 * for the string `'file'`.
 */
export function isFileSourcePin(ref: PinnedWorkspaceRef): boolean {
  return (ref.kind === 'query' || ref.kind === 'notebook') && ref.payload.source === 'file';
}

/** Analytics props derived from a built ref (mirrors `openWorkspacePin`). */
export function workspacePinTelemetry(ref: PinnedWorkspaceRef): WorkspacePinTelemetry {
  return {
    workspaceType: pinWorkspaceType(ref.kind),
    source: isFileSourcePin(ref) ? 'file' : 'snapshot',
  };
}
